/**
 * Maps SPA pathnames to stable Datadog RUM view names.
 *
 * AppRoutes calls `startDatadogView({ name: resolveDatadogViewName(...) })`
 * on every route change. Mapping the path explicitly (instead of letting
 * Datadog auto-detect from the URL) keeps dashboard cardinality stable
 * across deeply-segmented or query-string-laden routes and gives
 * dashboards a human-friendly grouping key.
 *
 * Convention:
 *   - Dot-case hierarchy mirroring the URL (`/ges/about-management`
 *     becomes `ges.about-management`).
 *   - No spaces, no slashes — DD widget filters mishandle both.
 *   - Multiple pathnames may collapse into the same view name when the
 *     routes are aliases (`/` and `/home` are both `home`).
 *
 * IMPORTANT: when adding a new <Route path="..."> in App.tsx, add the
 * matching entry here in the same PR. Unmapped routes fall through to
 * the raw pathname — the app keeps working but dashboards lose their
 * canonical grouping for that route.
 */
export const DATADOG_VIEW_NAMES: Readonly<Record<string, string>> = Object.freeze({
  '/': 'home',
  '/home': 'home',
  '/login': 'login',
  '/apps/edit': 'apps.edit',
  '/apps/search': 'apps.search',
  '/settings': 'settings',
  '/ges': 'ges',
  '/ges/about-sodexo': 'ges.about-sodexo',
  '/ges/about-management': 'ges.about-management',
  '/workforce-sizing': 'workforce-sizing',
  '/dashboards': 'dashboards',
  '/econtrat': 'econtrat',
  '/my-tasks': 'my-tasks',
  '/pas': 'pas',
  '/accessibility': 'accessibility',
  '/support-center': 'support-center',
});

/**
 * Wildcard route roots (registered as `<prefix>/*` in App.tsx). Any nested
 * path under one of these collapses to `<name>.detail` so per-record ids
 * (contract/site codes, etc.) can never explode RUM view cardinality or embed
 * business identifiers in `view.name`. The bare prefix itself is mapped in
 * DATADOG_VIEW_NAMES above.
 */
const DATADOG_VIEW_PREFIXES: Readonly<Record<string, string>> = Object.freeze({
  '/pas': 'pas',
});

// Path segments that look like identifiers — collapsed to ':id' so unmapped
// dynamic routes don't leak business identifiers or explode cardinality:
//   - pure numeric ids (`12345`);
//   - UUIDs;
//   - long (6+) alphanumeric codes that CONTAIN a digit (`BR017031`).
// The digit requirement keeps pure-alpha route names (e.g. `dashboards`)
// intact so a genuinely-unmapped route still surfaces as a readable path
// (the signal to extend DATADOG_VIEW_NAMES) rather than being masked as `:id`.
const DYNAMIC_SEGMENT_RE =
  /^(?:\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|(?=[A-Za-z0-9]{6,}$)(?=[A-Za-z0-9]*\d)[A-Za-z0-9]+)$/i;

const normalizeUnmappedPath = (pathname: string): string => {
  const normalized = pathname
    .split('/')
    .filter(Boolean)
    .map((segment) => (DYNAMIC_SEGMENT_RE.test(segment) ? ':id' : segment))
    .join('/');
  return `/${normalized}`;
};

/**
 * Looks up the canonical Datadog view name for a React Router pathname.
 *
 * Resolution order:
 *   1. exact match in DATADOG_VIEW_NAMES;
 *   2. wildcard-root bucket (`/pas/<id>` → `pas.detail`);
 *   3. fallback to a normalized path with dynamic segments collapsed to `:id`
 *      (still starts with `/` — the signal to extend DATADOG_VIEW_NAMES — but
 *      bounded so ids never create high-cardinality views).
 *
 * Input contract: callers pass `location.pathname` from react-router, which
 * never includes `?search` or `#hash`.
 */
/**
 * Redacts a full URL (or path) for a Datadog RUM `beforeSend`: collapses
 * id-like path segments to `:id` (same rule as unmapped view names) and strips
 * the query string + hash entirely. `view.name` is already a stable bucket, but
 * the SDK still stores the native `view.url` / `referrer` / resource URLs with
 * raw contract/site ids and query params — this keeps those facets low-
 * cardinality and free of business identifiers.
 *
 * Accepts absolute URLs (origin preserved) and origin-relative paths. Falls
 * back to a best-effort query/hash strip if the input can't be parsed.
 */
export const redactDynamicUrl = (rawUrl: string): string => {
  if (!rawUrl) return rawUrl;
  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(rawUrl);
  try {
    // Non-connecting placeholder base for parsing relative URLs — `.invalid` is
    // a reserved TLD (RFC 6761) that never resolves and `new URL()` makes no
    // request. https scheme only to satisfy S5332; the base scheme never reaches
    // the output (absolute inputs use their own origin; relative inputs return
    // only the path).
    const parsed = new URL(rawUrl, 'https://redacted.invalid');
    const path = normalizeUnmappedPath(parsed.pathname);
    return hasScheme ? `${parsed.origin}${path}` : path;
  } catch {
    return rawUrl.split(/[?#]/)[0];
  }
};

export const resolveDatadogViewName = (pathname: string): string => {
  if (!pathname) return pathname;

  const exact = DATADOG_VIEW_NAMES[pathname];
  if (exact) return exact;

  for (const [prefix, name] of Object.entries(DATADOG_VIEW_PREFIXES)) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return `${name}.detail`;
    }
  }

  return normalizeUnmappedPath(pathname);
};
